import { createClient } from '@supabase/supabase-js';
import { supabase, supabaseUrl, supabaseAnonKey } from '../lib/supabase';
import { AppPermission, AppRole } from '../types/permissions';
import {
  SystemGroup,
  SystemProgram,
  SystemGroupProgram,
  SystemUserGroup,
  SystemUserProgram,
  SystemUsers
} from '../types/sistema';
import { ROLE_PERMISSIONS } from './permissionsService';
import { adminAuthService } from './adminAuthService';
import { FirebaseAuthService, FirebaseUserProfile } from './firebaseAuthService';

export type { SystemProgram };

export interface UserPermissionProfile {
  id: number;
  name: string;
  login: string;
  email: string | null;
  active: string | null;
  auth_user_id?: string | null;
  role: AppRole;
  system_unit_id?: number | null;
  groups: { id: number; name: string }[];
  direct_programs: { id: number; name: string; controller: string }[];
  effective_programs: { id: number; name: string; controller: string }[];
  custom_permissions?: AppPermission[];
  /**
   * true = o admin fixou uma lista de permissões só para este usuário (via
   * "Editar Permissões"), que passa a ignorar mudanças futuras no grupo.
   * false/undefined = segue o(s) grupo(s) - qualquer mudança em "Editar
   * Grupo" (system_group_program) já vale pra ele automaticamente.
   */
  permissoes_customizadas?: boolean;
  deleted?: boolean;
}

export interface GroupWithPrograms {
  id: number;
  name: string;
  uuid?: string | null;
  program_ids: number[];
}

// Programas padrão do sistema
export const DEFAULT_SYSTEM_PROGRAMS: Omit<SystemProgram, 'id'>[] = [
  { name: 'Dashboard Executivo & Métricas', controller: 'DashboardController' },
  { name: 'Ordens de Serviço & Viagens', controller: 'OrdemServicoList' },
  { name: 'Roteirização & Sequenciamento', controller: 'RoteiroController' },
  { name: 'Pessoas, Clientes & Motoristas', controller: 'PessoaList' },
  { name: 'Cadastro de Pessoas (Edição)', controller: 'PessoaForm' },
  { name: 'Gestão de Frotas & Veículos', controller: 'VeiculoList' },
  { name: 'Financeiro, Contas & Faturas', controller: 'ContaList' },
  { name: 'Medições de Clientes & Agregados', controller: 'MedicaoList' },
  { name: 'Propostas Comerciais', controller: 'PropostaList' },
  { name: 'Administração de Usuários & RBAC', controller: 'SystemUsersList' },
  { name: 'Grupos & Perfis de Acesso', controller: 'SystemGroupList' },
  { name: 'Área do Motorista (App PWA)', controller: 'AreaMotoristaController' },
  { name: 'Área do Cliente (Tracking)', controller: 'AreaClienteController' },
  { name: 'Logs & Auditoria do Sistema', controller: 'SystemLogController' }
];

// Grupos padrão
export const DEFAULT_SYSTEM_GROUPS: { id: number; name: string; programs: string[] }[] = [
  {
    id: 1,
    name: 'Administradores (Full Access)',
    programs: [
      'DashboardController',
      'OrdemServicoList',
      'RoteiroController',
      'PessoaList',
      'PessoaForm',
      'VeiculoList',
      'ContaList',
      'MedicaoList',
      'PropostaList',
      'SystemUsersList',
      'SystemGroupList',
      'AreaMotoristaController',
      'AreaClienteController',
      'SystemLogController'
    ]
  },
  {
    id: 2,
    name: 'Operacional & Expedição',
    programs: [
      'DashboardController',
      'OrdemServicoList',
      'RoteiroController',
      'PessoaList',
      'VeiculoList',
      'PropostaList'
    ]
  },
  {
    id: 3,
    name: 'Financeiro & Controladoria',
    programs: [
      'DashboardController',
      'ContaList',
      'MedicaoList',
      'PessoaList',
      'OrdemServicoList'
    ]
  },
  {
    id: 4,
    name: 'Comercial & Propostas',
    programs: [
      'DashboardController',
      'PropostaList',
      'PessoaList',
      'OrdemServicoList',
      'MedicaoList'
    ]
  },
  {
    id: 5,
    name: 'Usuários Padrão (Consulta)',
    programs: [
      'DashboardController',
      'OrdemServicoList',
      'PessoaList',
      'VeiculoList'
    ]
  },
  {
    id: 6,
    name: 'Cliente Obra (Painel de Solicitação)',
    programs: [
      'AreaClienteController'
    ]
  },
  {
    id: 7,
    name: 'Motorista CLT / Próprio',
    programs: [
      'AreaMotoristaController'
    ]
  },
  {
    id: 8,
    name: 'Agregado / Autônomo',
    programs: [
      'AreaMotoristaController'
    ]
  }
];

// Mapeamento de Controllers para Permissões do React
export const CONTROLLER_TO_PERMISSIONS: Record<string, AppPermission[]> = {
  DashboardController: ['view_dashboard', 'view_dashboard_kpis', 'view_dashboard_charts'],
  OrdemServicoList: [
    'view_orders', 'create_orders', 'manage_orders', 'approve_orders', 'print_os',
    'view_order_prices', 'edit_order_prices', 'view_order_repasse', 'edit_order_repasse', 'view_order_margin'
  ],
  RoteiroController: ['view_routes', 'create_routes', 'manage_routes'],
  PessoaList: ['view_people', 'create_people', 'manage_people'],
  PessoaForm: ['view_people', 'create_people', 'manage_people'],
  VeiculoList: ['view_vehicles', 'create_vehicles', 'manage_vehicles'],
  ContaList: ['view_financial', 'create_financial', 'manage_financial', 'pay_financial', 'delete_financial', 'view_financial_dre'],
  MedicaoList: ['view_medicoes', 'create_medicoes', 'manage_medicoes'],
  PropostaList: ['view_proposals', 'manage_proposals', 'approve_proposals'],
  SystemUsersList: ['manage_system_settings', 'manage_users', 'create_user', 'edit_user', 'delete_user', 'manage_permissions'],
  SystemGroupList: ['manage_system_settings', 'manage_groups', 'manage_permissions'],
  AreaMotoristaController: ['view_routes'],
  AreaClienteController: ['view_orders'],
  SystemLogController: ['view_audit_logs']
};

// Base inicial realista de usuários da Voope Transportes (sem PII pessoal hardcoded)
export const DEFAULT_REAL_USERS: UserPermissionProfile[] = [
  {
    id: 9001,
    name: 'Administrador Voope',
    login: 'admin',
    email: 'admin@voopetrans.com.br',
    active: 'Y',
    auth_user_id: 'auth_master_admin',
    role: 'admin',
    system_unit_id: 1,
    groups: [{ id: 1, name: 'Administradores (Full Access)' }],
    direct_programs: [],
    effective_programs: [],
    custom_permissions: ROLE_PERMISSIONS.admin
  },
  {
    id: 9011,
    name: 'Equipe Financeiro',
    login: 'financeiro',
    email: 'financeiro@voopetrans.com.br',
    active: 'Y',
    auth_user_id: 'auth_financeiro_voope',
    role: 'user',
    system_unit_id: 1,
    groups: [{ id: 3, name: 'Financeiro & Controladoria' }],
    direct_programs: [],
    effective_programs: [],
    custom_permissions: [
      'view_dashboard',
      'view_financial', 'create_financial', 'manage_financial', 'pay_financial', 'view_financial_dre',
      'view_medicoes', 'create_medicoes', 'manage_medicoes',
      'view_orders', 'view_order_prices'
    ]
  },
  {
    id: 9012,
    name: 'Equipe Comercial',
    login: 'comercial',
    email: 'comercial@voopetrans.com.br',
    active: 'Y',
    auth_user_id: 'auth_comercial_voope',
    role: 'user',
    system_unit_id: 1,
    groups: [{ id: 2, name: 'Operacional & Expedição' }, { id: 4, name: 'Comercial & Propostas' }],
    direct_programs: [],
    effective_programs: [],
    custom_permissions: [
      'view_dashboard',
      'view_orders', 'manage_orders', 'create_orders',
      'view_proposals', 'manage_proposals',
      'view_people', 'create_people', 'manage_people',
      'view_medicoes'
    ]
  },
  {
    id: 9013,
    name: 'Gerente Operacional',
    login: 'operacional',
    email: 'operacional@voopetrans.com.br',
    active: 'Y',
    auth_user_id: 'auth_operacional_voope',
    role: 'manager',
    system_unit_id: 1,
    groups: [{ id: 2, name: 'Operacional & Expedição' }],
    direct_programs: [],
    effective_programs: [],
    custom_permissions: ROLE_PERMISSIONS.manager
  },
  {
    id: 9015,
    name: 'Atendimento Voope',
    login: 'contato.voope',
    email: 'contato@voopetrans.com.br',
    active: 'Y',
    auth_user_id: 'auth_contato_voope',
    role: 'user',
    system_unit_id: 1,
    groups: [{ id: 4, name: 'Comercial & Propostas' }],
    direct_programs: [],
    effective_programs: [],
    custom_permissions: ROLE_PERMISSIONS.user
  },
  {
    id: 9005,
    name: 'Motorista Exemplo',
    login: 'motorista',
    email: 'motorista@voopetrans.com.br',
    active: 'Y',
    auth_user_id: 'auth_motorista_voope',
    role: 'driver',
    system_unit_id: 1,
    groups: [{ id: 7, name: 'Motorista CLT / Próprio' }],
    direct_programs: [],
    effective_programs: [],
    custom_permissions: ['view_routes']
  },
  {
    id: 9006,
    name: 'Cliente Obra Exemplo',
    login: 'cliente.obra',
    email: 'cliente@voopetrans.com.br',
    active: 'Y',
    auth_user_id: 'auth_cliente_voope',
    role: 'client',
    system_unit_id: 1,
    groups: [{ id: 6, name: 'Cliente Obra (Painel de Solicitação)' }],
    direct_programs: [],
    effective_programs: [],
    custom_permissions: ['view_orders', 'create_orders']
  }
];

// Helper para mapear programas dos grupos nos fallbacks de getSystemGroups
const getGroupDefaultProgramIds = (groupProgramControllers: string[]): number[] => {
  return DEFAULT_SYSTEM_PROGRAMS
    .map((p, idx) => ({ id: idx + 1, controller: p.controller }))
    .filter(p => groupProgramControllers.includes(p.controller))
    .map(p => p.id);
};

export const userManagementService = {
  /**
   * Retorna lista de todos os grupos com os IDs dos programas vinculados
   */
  async getSystemGroups(): Promise<GroupWithPrograms[]> {
    try {
      const { data: groups, error: groupErr } = await supabase
        .from('system_group')
        .select('id, name')
        .order('id');

      if (groupErr || !groups || groups.length === 0) {
        return DEFAULT_SYSTEM_GROUPS.map(g => ({
          id: g.id,
          name: g.name,
          program_ids: getGroupDefaultProgramIds(g.programs)
        }));
      }

      const { data: groupPrograms } = await supabase
        .from('system_group_program')
        .select('system_group_id, system_program_id');

      const programMap: Record<number, number[]> = {};
      (groupPrograms || []).forEach((gp: any) => {
        if (!programMap[gp.system_group_id]) {
          programMap[gp.system_group_id] = [];
        }
        programMap[gp.system_group_id].push(gp.system_program_id);
      });

      return groups.map((g: any) => ({
        id: g.id,
        name: g.name,
        uuid: g.uuid,
        program_ids: programMap[g.id] || getGroupDefaultProgramIds(
          DEFAULT_SYSTEM_GROUPS.find(dg => dg.id === g.id)?.programs || []
        )
      }));
    } catch (err) {
      console.warn('Fallback grupos do sistema:', err);
      return DEFAULT_SYSTEM_GROUPS.map(g => ({
        id: g.id,
        name: g.name,
        program_ids: getGroupDefaultProgramIds(g.programs)
      }));
    }
  },

  /**
   * Retorna todos os programas cadastrados no sistema
   */
  async getSystemPrograms(): Promise<SystemProgram[]> {
    try {
      const { data: programs, error } = await supabase
        .from('system_program')
        .select('id, name, controller')
        .order('id');

      if (error || !programs || programs.length === 0) {
        return DEFAULT_SYSTEM_PROGRAMS.map((p, idx) => ({
          id: idx + 1,
          name: p.name,
          controller: p.controller
        }));
      }

      return programs;
    } catch (err) {
      return DEFAULT_SYSTEM_PROGRAMS.map((p, idx) => ({
        id: idx + 1,
        name: p.name,
        controller: p.controller
      }));
    }
  },

  /**
   * Retorna todos os usuários com seus grupos, programas e permissões calculadas
   */
  async getUsuariosComPermissoes(): Promise<UserPermissionProfile[]> {
    try {
      const [groups, programs, firestoreUsers, deletedUserIdsList, deletedPayloads] = await Promise.all([
        this.getSystemGroups(),
        this.getSystemPrograms(),
        FirebaseAuthService.getAllFirestoreUsers(),
        FirebaseAuthService.getDeletedUserIdsFromFirestore(),
        FirebaseAuthService.getDeletedUsersPayloadFromFirestore()
      ]);

      const groupMap = new Map<number, GroupWithPrograms>(groups.map(g => [g.id, g]));
      const programMap = new Map<number, SystemProgram>(programs.map(p => [p.id, p]));
      const deletedSet = new Set(deletedUserIdsList.map(item => String(item).trim().toLowerCase()));

      // 1. Tenta buscar do Supabase
      let dbUsers: any[] = [];
      try {
        const { data: users, error: uErr } = await supabase
          .from('system_users')
          .select('id, name, login, email, active, auth_user_id, system_unit_id')
          .order('id');

        if (!uErr && users && users.length > 0) {
          dbUsers = users;
        }
      } catch (e) {
        // RLS ou conexão
      }

      // 2. Monta mapa de usuários a partir do Supabase e Firestore (sem injetar defaults mockados)
      const userMap = new Map<number | string, any>();

      // Sobrescreve/mescla com dados do banco Supabase se houver
      dbUsers.forEach(u => {
        const emailNorm = (u.email || '').trim().toLowerCase();
        
        const existing = userMap.get(u.id);
        userMap.set(u.id, {
          ...existing,
          id: u.id,
          name: u.name || existing?.name,
          login: u.login || existing?.login,
          email: u.email || existing?.email,
          active: u.active || existing?.active || 'Y',
          auth_user_id: u.auth_user_id || existing?.auth_user_id
        });
      });

      // Mescla com os usuários cadastrados no Firestore (fonte primária de verdade)
      firestoreUsers.forEach((fu, idx) => {
        const normEmail = (fu.email || '').trim().toLowerCase();
        if (!normEmail) return;

        // Procura se já existe por id, email ou auth_user_id
        let existingKey: number | string | undefined;
        userMap.forEach((uVal, uKey) => {
          if (
            (fu.id && Number(uVal.id) === Number(fu.id)) ||
            (uVal.email || '').trim().toLowerCase() === normEmail ||
            uVal.auth_user_id === fu.uid
          ) {
            existingKey = uKey;
          }
        });

        const fuName = fu.displayName || fu.name;

        if (existingKey !== undefined) {
          const existing = userMap.get(existingKey);
          userMap.set(existingKey, {
            ...existing,
            auth_user_id: fu.uid,
            name: fuName || existing?.name || normEmail.split('@')[0],
            login: fu.login || existing?.login || normEmail.split('@')[0],
            email: normEmail,
            role: fu.role || existing?.role || 'user',
            active: fu.active ? 'Y' : 'N',
            custom_permissions: fu.custom_permissions !== undefined ? fu.custom_permissions : existing?.custom_permissions,
            groups: fu.groupIds || existing?.groups || [2]
          });
        } else {
          // Cria registro para novo usuário cadastrado no Firestore sem colisão de IDs
          const existingNumericIds = Array.from(userMap.keys())
            .map(k => Number(k))
            .filter(n => !isNaN(n) && n > 0 && n < 10000);
          const maxNumericId = existingNumericIds.length > 0 ? Math.max(...existingNumericIds) : 100;
          const nextId = fu.id || (maxNumericId + idx + 1);

          userMap.set(nextId, {
            id: nextId,
            name: fuName || normEmail.split('@')[0],
            login: fu.login || normEmail.split('@')[0],
            email: normEmail,
            active: fu.active ? 'Y' : 'N',
            auth_user_id: fu.uid,
            role: fu.role || 'user',
            groups: fu.groupIds || [2],
            custom_permissions: fu.custom_permissions
          });
        }
      });

      // Adiciona usuários que só existem em deleted_users
      deletedPayloads.forEach(dp => {
        const emailNorm = (dp.email || '').trim().toLowerCase();
        let existingKey: number | string | undefined;
        userMap.forEach((uVal, uKey) => {
          if (
            (dp.id && Number(uVal.id) === Number(dp.id)) ||
            (emailNorm && (uVal.email || '').trim().toLowerCase() === emailNorm) ||
            (dp.uid && uVal.auth_user_id === dp.uid)
          ) {
            existingKey = uKey;
          }
        });

        if (existingKey === undefined) {
          const nextId = dp.id || (Math.floor(Math.random() * 10000) + 20000); // ID provisório se não tiver
          userMap.set(nextId, {
            id: nextId,
            name: dp.email ? dp.email.split('@')[0] : 'Usuário Excluído',
            login: dp.email ? dp.email.split('@')[0] : 'excluido',
            email: emailNorm,
            active: 'N',
            auth_user_id: dp.uid,
            role: 'user',
            groups: [2],
            custom_permissions: []
          });
        }
      });

      const allMergedUsers = Array.from(userMap.values()).map(u => {
        const emailNorm = (u.email || '').trim().toLowerCase();
        const isDeleted = deletedSet.has(String(u.id)) || deletedSet.has(emailNorm) || deletedSet.has(u.auth_user_id);
        return {
          ...u,
          deleted: isDeleted
        };
      });

      // Mapeia para UserPermissionProfile completo
      return allMergedUsers.map((u: any) => {
        const rawGroupIds: number[] = Array.isArray(u.groups) ? u.groups : (Array.isArray(u.groupIds) ? u.groupIds : [2]);
        const groupIds = rawGroupIds.map(Number);
        const userGroups = groupIds.map(gid => {
          const grp = groupMap.get(gid);
          return { id: gid, name: grp ? grp.name : `Grupo #${gid}` };
        });

        // Coleta programas herdados dos grupos
        const groupProgramIds = new Set<number>();
        groupIds.forEach(gid => {
          const grp = groupMap.get(gid);
          if (grp && grp.program_ids) {
            grp.program_ids.forEach(pid => groupProgramIds.add(pid));
          }
        });

        const directPrograms = (u.direct_programs || [])
          .map((p: any) => typeof p === 'number' ? programMap.get(p) : p)
          .filter(Boolean) as SystemProgram[];

        const allEffectiveProgramIds = new Set([...Array.from(groupProgramIds), ...directPrograms.map(p => p.id)]);
        
        const isAdmin = groupIds.includes(1) || u.login?.toLowerCase().includes('admin') || u.role === 'admin';
        if (isAdmin) {
          programs.forEach(p => allEffectiveProgramIds.add(p.id));
        }

        const effectivePrograms = Array.from(allEffectiveProgramIds)
          .map(pid => programMap.get(pid))
          .filter(Boolean) as SystemProgram[];

        const permissionsSet = new Set<AppPermission>();
        effectivePrograms.forEach(prog => {
          const mapped = CONTROLLER_TO_PERMISSIONS[prog.controller];
          if (mapped) {
            mapped.forEach(p => permissionsSet.add(p));
          }
        });

        let role: AppRole = u.role || 'user';
        const groupNames = userGroups.map(g => g.name.toLowerCase());
        if (isAdmin) {
          role = 'admin';
        } else if (groupIds.includes(6) || groupNames.some(n => n.includes('cliente'))) {
          role = 'client';
        } else if (groupIds.some(id => [7, 8].includes(id)) || groupNames.some(n => n.includes('motorista') || n.includes('agregado'))) {
          role = 'driver';
        } else if (groupIds.some(id => [2, 3, 4].includes(id))) {
          role = u.role || 'user';
        }

        // Só está em "modo customizado" se alguém explicitamente salvou uma
        // lista granular pra esse usuário (via "Editar Permissões" com a
        // opção ligada). Sem isso, o acesso é 100% derivado do(s) grupo(s).
        const temPermissoesCustomizadas = u.custom_permissions !== undefined && Array.isArray(u.custom_permissions);

        const finalPermissions = temPermissoesCustomizadas
          ? u.custom_permissions
          : (Array.from(permissionsSet).length > 0 ? Array.from(permissionsSet) : (ROLE_PERMISSIONS[role] || []));

        return {
          id: u.id,
          name: u.name,
          login: u.login,
          email: u.email,
          active: u.active || 'Y',
          auth_user_id: u.auth_user_id || `auth_${u.id}`,
          system_unit_id: u.system_unit_id || 1,
          groups: userGroups,
          direct_programs: directPrograms,
          effective_programs: effectivePrograms,
          role,
          custom_permissions: finalPermissions,
          permissoes_customizadas: temPermissoesCustomizadas
        };
      });
    } catch (err) {
      console.error('Erro ao buscar usuários e permissões:', err);
      return DEFAULT_REAL_USERS;
    }
  },

  /**
   * Alterna o status ativo ('Y'/'S' vs 'N') de um usuário
   */
  async toggleUserStatus(userId: number, currentActiveStatus: string | null): Promise<boolean> {
    try {
      const isCurrentlyActive = currentActiveStatus === 'Y' || currentActiveStatus === 'S';
      const newStatus = isCurrentlyActive ? 'N' : 'Y';

      const res = await adminAuthService.atualizarUsuario(userId, { active: newStatus });
      return res.success;
    } catch (e) {
      console.error('Erro ao alternar status do usuário:', e);
      return false;
    }
  },

  /**
   * Exclui um usuário do sistema em system_users, Firestore e Auth
   */
  async excluirUsuario(userId: number, email?: string, uid?: string): Promise<{ success: boolean, message?: string }> {
    try {
      const res = await adminAuthService.excluirUsuario(userId, email, uid);
      return res;
    } catch (e: any) {
      console.error('Erro ao excluir usuário:', e);
      return { success: false, message: e.message };
    }
  },

  /**
   * Restaura um usuário previamente excluído e reativa seu perfil no sistema
   */
  async restaurarUsuario(userId: number, email?: string): Promise<{ success: boolean, message?: string }> {
    try {
      return await adminAuthService.restaurarUsuario(userId, email);
    } catch (e: any) {
      console.error('Erro ao restaurar usuário:', e);
      return { success: false, message: e.message };
    }
  },

  /**
   * Sincroniza e restaura todos os usuários existentes no Firebase
   */
  async sincronizarUsuariosFirebase(): Promise<{ success: boolean; message?: string; count?: number }> {
    try {
      return await adminAuthService.sincronizarUsuariosFirebase();
    } catch (e: any) {
      console.error('Erro ao sincronizar usuários do Firebase:', e);
      return { success: false, message: e.message };
    }
  },

  /**
   * Salva os programas permitidos para um grupo em system_group_program
   */
  async salvarProgramasDoGrupo(groupId: number, programIds: number[]): Promise<boolean> {
    try {
      const { error: delErr } = await supabase.from('system_group_program').delete().eq('system_group_id', groupId);
      if (delErr) {
        console.error('Erro ao deletar programas antigos do grupo:', delErr);
        return false;
      }
      if (programIds.length > 0) {
        const records = programIds.map(pid => ({
          system_group_id: groupId,
          system_program_id: pid
        }));
        const { error: insErr } = await supabase.from('system_group_program').insert(records);
        if (insErr) {
          console.error('Erro ao inserir programas do grupo:', insErr);
          return false;
        }
      }
      return true;
    } catch (err) {
      console.error('Erro ao salvar programas do grupo no Supabase:', err);
      return false;
    }
  },

  /**
   * Salva as permissões e grupos de um usuário diretamente no Firestore e Supabase
   *
   * `usarPermissoesCustomizadas` decide o que fica valendo pra esse usuário:
   * - false (padrão): grava `custom_permissions: null`. O acesso passa a ser
   *   100% derivado ao vivo do(s) grupo(s) selecionado(s) - se alguém depois
   *   editar os programas de um grupo em "Editar Grupo", esse usuário recebe
   *   o novo acesso automaticamente, sem precisar reabrir esta tela.
   * - true: grava a lista granular escolhida na aba "Permissões Granulares"
   *   como uma exceção fixa para este usuário, que passa a ignorar mudanças
   *   futuras no grupo até essa opção ser desmarcada de novo.
   */
  async salvarPermissoesUsuario(
    userId: number,
    role: AppRole,
    permissions: AppPermission[],
    groupIds: number[],
    directProgramIds: number[],
    activeStatus: string,
    usarPermissoesCustomizadas: boolean = true
  ): Promise<boolean> {
    let success = false;
    try {
      // 1. Sincroniza e persiste diretamente no Firestore
      try {
        const allUsers = await this.getUsuariosComPermissoes();
        const targetUser = allUsers.find(u => u.id === userId);
        if (targetUser) {
          const email = (targetUser.email || targetUser.login || '').trim().toLowerCase();
          const authUid = targetUser.auth_user_id;

          const profileData: Partial<FirebaseUserProfile> = {
            id: userId,
            email,
            name: targetUser.name,
            displayName: targetUser.name,
            login: targetUser.login,
            role,
            custom_permissions: usarPermissoesCustomizadas ? permissions : null,
            groupIds,
            active: activeStatus === 'Y'
          };

          // Salva por UID no Firestore se for UID válido do Firebase Auth
          if (authUid && !authUid.startsWith('auth_') && authUid.length > 15) {
            await FirebaseAuthService.saveUserProfile(authUid, profileData);
            success = true;
          }

          // Salva também no Firestore buscando documento por e-mail ou id
          const firestoreUsers = await FirebaseAuthService.getAllFirestoreUsers();
          const matchingDocs = firestoreUsers.filter(
            fu => (email && fu.email && fu.email.trim().toLowerCase() === email) || (fu.id && fu.id === userId)
          );
          
          if (matchingDocs.length > 0) {
            for (const doc of matchingDocs) {
              await FirebaseAuthService.saveUserProfile(doc.uid, profileData);
            }
            success = true;
          }

          // Se nenhum documento prévio foi encontrado, cria um documento sob o e-mail ou ID
          if (!success && email) {
            await FirebaseAuthService.saveUserProfile(email, profileData);
            success = true;
          } else if (!success) {
            await FirebaseAuthService.saveUserProfile(`user_${userId}`, profileData);
            success = true;
          }
        }
      } catch (fsErr) {
        console.error('Erro ao sincronizar permissões no Firestore:', fsErr);
      }

      // 2. Atualizar no Supabase se possível
      try {
        const { error: updateErr } = await supabase
          .from('system_users')
          .update({ active: activeStatus })
          .eq('id', userId);

        if (!updateErr) {
          await supabase.from('system_user_group').delete().eq('system_user_id', userId);
          if (groupIds.length > 0) {
            const records = groupIds.map(gid => ({
              system_user_id: userId,
              system_group_id: gid
            }));
            await supabase.from('system_user_group').insert(records);
          }
          success = true;
        }
      } catch (e) {
        console.warn('Aviso Supabase ao salvar permissões:', e);
      }

      return success;
    } catch (err) {
      console.error('Erro ao salvar permissões do usuário:', err);
      return false;
    }
  },

  /**
   * Atualiza os dados básicos de um usuário (nome, login, email, senha e perfil) usando a Admin Auth API
   */
  async atualizarDadosUsuario(
    userId: number,
    authUserId: string | null,
    userData: {
      name: string;
      login: string;
      email: string;
      password?: string;
      groupIds?: number[];
      active?: string;
    }
  ): Promise<{ success: boolean; message?: string }> {
    return adminAuthService.atualizarUsuario(userId, {
      name: userData.name,
      login: userData.login,
      email: userData.email,
      password: userData.password,
      groupIds: userData.groupIds,
      active: userData.active
    });
  },

  /**
   * Cria um novo usuário em system_users e vincula ao Supabase Auth
   */
  async criarUsuario(userData: {
    name: string;
    login: string;
    email: string;
    password?: string;
    role: AppRole;
    groupIds: number[];
  }): Promise<{ success: boolean; message?: string }> {
    return adminAuthService.criarUsuario({
      name: userData.name,
      login: userData.login,
      email: userData.email,
      password: userData.password,
      role: userData.role,
      groupIds: userData.groupIds,
      active: 'Y'
    });
  },

  /**
   * Sincroniza o usuário logado atual do Supabase Auth com system_users
   */
  async sincronizarUsuarioSupabaseAuth(): Promise<SystemUsers | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: existingUser } = await supabase
        .from('system_users')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();

      if (existingUser) {
        if (!existingUser.auth_user_id) {
          await supabase
            .from('system_users')
            .update({ auth_user_id: user.id })
            .eq('id', existingUser.id);
        }
        return existingUser;
      }

      return null;
    } catch (e) {
      return null;
    }
  },

  /**
   * Retorna o script SQL completo de migração para o Supabase (Strict Security & Fail-Closed RLS)
   */
  getMigrationSQL(): string {
    return `-- SCRIPT SQL DE SEGURANÇA E ESTRUTURA SUPABASE (VOOPE TRANSPORTES)
-- Execute este script no SQL Editor do Dashboard do Supabase para aplicar a estrutura com RLS Fail-Closed.

-- 1. Criação das Tabelas Principais
CREATE TABLE IF NOT EXISTS public.system_users (
  id BIGINT PRIMARY KEY,
  name VARCHAR(255) NOT NULL,
  login VARCHAR(100) NOT NULL UNIQUE,
  email VARCHAR(255) UNIQUE,
  password VARCHAR(255) DEFAULT '***',
  active VARCHAR(1) DEFAULT 'Y',
  auth_user_id UUID REFERENCES auth.users(id) ON DELETE SET NULL,
  system_unit_id BIGINT DEFAULT 1,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS public.system_group (
  id BIGINT PRIMARY KEY,
  name VARCHAR(100) NOT NULL,
  uuid UUID DEFAULT gen_random_uuid()
);

CREATE TABLE IF NOT EXISTS public.system_program (
  id BIGINT PRIMARY KEY,
  name VARCHAR(255) NOT NULL,
  controller VARCHAR(100) NOT NULL UNIQUE
);

CREATE TABLE IF NOT EXISTS public.system_user_group (
  system_user_id BIGINT REFERENCES public.system_users(id) ON DELETE CASCADE,
  system_group_id BIGINT REFERENCES public.system_group(id) ON DELETE CASCADE,
  PRIMARY KEY (system_user_id, system_group_id)
);

CREATE TABLE IF NOT EXISTS public.system_user_program (
  system_user_id BIGINT REFERENCES public.system_users(id) ON DELETE CASCADE,
  system_program_id BIGINT REFERENCES public.system_program(id) ON DELETE CASCADE,
  PRIMARY KEY (system_user_id, system_program_id)
);

CREATE TABLE IF NOT EXISTS public.system_group_program (
  system_group_id BIGINT REFERENCES public.system_group(id) ON DELETE CASCADE,
  system_program_id BIGINT REFERENCES public.system_program(id) ON DELETE CASCADE,
  PRIMARY KEY (system_group_id, system_program_id)
);

CREATE TABLE IF NOT EXISTS public.system_preference (
  id VARCHAR(255) PRIMARY KEY,
  preference TEXT NOT NULL
);

-- 2. Concessão Estrita de Permissões Schema / Tabelas (REVOGANDO ACESSO ANÔNIMO DE ESCRITA/LEITURA)
REVOKE ALL ON ALL TABLES IN SCHEMA public FROM anon;
REVOKE ALL ON ALL SEQUENCES IN SCHEMA public FROM anon;
REVOKE ALL ON ALL ROUTINES IN SCHEMA public FROM anon;

GRANT USAGE ON SCHEMA public TO authenticated, service_role;
GRANT ALL ON ALL TABLES IN SCHEMA public TO authenticated, service_role;
GRANT ALL ON ALL SEQUENCES IN SCHEMA public TO authenticated, service_role;

ALTER DEFAULT PRIVILEGES IN SCHEMA public GRANT ALL ON TABLES TO authenticated, service_role;
ALTER DEFAULT PRIVILEGES IN SCHEMA public GRANT ALL ON SEQUENCES TO authenticated, service_role;

-- 3. Funções Trigger e Segurança
CREATE OR REPLACE FUNCTION public.handle_new_auth_user()
RETURNS trigger
LANGUAGE plpgsql
SECURITY DEFINER
SET search_path = public, pg_temp
AS $$
DECLARE
  next_id integer;
  existing_user_id integer;
BEGIN
  SELECT id INTO existing_user_id
  FROM public.system_users
  WHERE LOWER(email) = LOWER(new.email)
  LIMIT 1;

  IF existing_user_id IS NOT NULL THEN
    UPDATE public.system_users
    SET auth_user_id = new.id
    WHERE id = existing_user_id AND auth_user_id IS NULL;
  ELSE
    SELECT COALESCE(MAX(id), 0) + 1 INTO next_id FROM public.system_users;

    INSERT INTO public.system_users (id, name, login, email, auth_user_id, active, password)
    VALUES (
      next_id,
      COALESCE(new.raw_user_meta_data->>'full_name', split_part(new.email, '@', 1)),
      split_part(new.email, '@', 1),
      new.email,
      new.id,
      'Y',
      '***'
    );

    INSERT INTO public.system_user_group (system_user_id, system_group_id)
    VALUES (next_id, 2)
    ON CONFLICT DO NOTHING;
  END IF;

  RETURN new;
EXCEPTION WHEN OTHERS THEN
  RETURN new;
END;
$$;

REVOKE ALL ON FUNCTION public.handle_new_auth_user() FROM PUBLIC, anon, authenticated;
GRANT EXECUTE ON FUNCTION public.handle_new_auth_user() TO postgres, service_role;

-- 3b. Função de Verificação Admin em SECURITY INVOKER (FAIL CLOSED)
CREATE OR REPLACE FUNCTION public.is_admin_user()
RETURNS boolean
LANGUAGE plpgsql
SECURITY INVOKER
SET search_path = public, pg_temp
AS $$
DECLARE
  v_is_admin boolean := false;
BEGIN
  -- FAIL CLOSED: Se não houver UID autenticado, NEGA o acesso
  IF auth.uid() IS NULL THEN
    RETURN false;
  END IF;

  SELECT true INTO v_is_admin
  FROM public.system_users su
  JOIN public.system_user_group sug ON sug.system_user_id = su.id
  WHERE su.auth_user_id = auth.uid()
    AND sug.system_group_id = 1
    AND (su.active = 'Y' OR su.active = 'ativo')
  LIMIT 1;

  RETURN COALESCE(v_is_admin, false);
END;
$$;

GRANT EXECUTE ON FUNCTION public.is_admin_user() TO authenticated, service_role;

CREATE OR REPLACE FUNCTION public.get_current_pessoa_id()
RETURNS integer
LANGUAGE plpgsql
SECURITY INVOKER
SET search_path = public, pg_temp
AS $$
DECLARE
  v_pessoa_id integer;
BEGIN
  IF auth.uid() IS NULL THEN
    RETURN NULL;
  END IF;

  SELECT p.id INTO v_pessoa_id
  FROM public.pessoa p
  JOIN public.system_users su ON su.id = p.system_user_id
  WHERE su.auth_user_id = auth.uid()
  LIMIT 1;

  RETURN v_pessoa_id;
END;
$$;

GRANT EXECUTE ON FUNCTION public.get_current_pessoa_id() TO authenticated, service_role;

-- 4. Habilitação de RLS e Políticas de Acesso Autenticado Restrito
DO $$ 
DECLARE 
  t text;
BEGIN
  FOR t IN 
    SELECT table_name 
    FROM information_schema.tables 
    WHERE table_schema = 'public' AND table_type = 'BASE TABLE' 
  LOOP
    EXECUTE format('ALTER TABLE public.%I ENABLE ROW LEVEL SECURITY;', t);
    EXECUTE format('DROP POLICY IF EXISTS "allow_read_%I" ON public.%I;', t, t);
    EXECUTE format('DROP POLICY IF EXISTS "allow_insert_%I" ON public.%I;', t, t);
    EXECUTE format('DROP POLICY IF EXISTS "allow_update_%I" ON public.%I;', t, t);
    EXECUTE format('DROP POLICY IF EXISTS "allow_delete_%I" ON public.%I;', t, t);
    EXECUTE format('DROP POLICY IF EXISTS "Acesso Autenticado %I" ON public.%I;', t, t);

    -- Políticas de Acesso Apenas para Usuários Autenticados ou Service Role
    EXECUTE format('CREATE POLICY "allow_read_%I" ON public.%I FOR SELECT TO authenticated, service_role USING (auth.role() IN (''authenticated'', ''service_role''));', t, t);
    EXECUTE format('CREATE POLICY "allow_insert_%I" ON public.%I FOR INSERT TO authenticated, service_role WITH CHECK (auth.role() IN (''authenticated'', ''service_role''));', t, t);
    EXECUTE format('CREATE POLICY "allow_update_%I" ON public.%I FOR UPDATE TO authenticated, service_role USING (auth.role() IN (''authenticated'', ''service_role'')) WITH CHECK (auth.role() IN (''authenticated'', ''service_role''));', t, t);
    EXECUTE format('CREATE POLICY "allow_delete_%I" ON public.%I FOR DELETE TO authenticated, service_role USING (auth.role() IN (''authenticated'', ''service_role''));', t, t);
  END LOOP;
END $$;
`;
  }
};
